/**
 * Меню за шаблоном WAI-ARIA Menu Button (components.md, розд. 8): ↑ ↓ Home End, Enter/Space
 * обирає, Esc закриває й повертає фокус на кнопку, клік поза меню закриває. Місце, `Esc` і «клік
 * поза» дає примітив шару `<Popover>` (розд. 8a): меню під кнопкою чи над нею, у межах вікна.
 * Свій мінімум замість React Aria: висновок spike — розд. «Що вклалося». Пункти, клавіші й фокус
 * відкритого меню — `MenuList.tsx`, у лінивому модулі шарів (`Popover.tsx`, «Лінива поведінка»).
 *
 * - **Пункти під час відкриття:** `items` — масив або функція; функцію викликають лише для відкритого
 *   меню, тож картка дошки й рядок списку не рахують права й стан на кожен рендер (KAN-11, LST-23).
 * - **Ліниві пункти** (#1244): функція дає `null`, поки пункти власника ще вантажаться (`onOpen`
 *   рядка списку й меню «?»), — меню чекає їх, як і модуль шарів. Проміс `onOpen` відхилено, а меню
 *   ще відкрите — меню закривається, `onOpenFailed` власника дає тост; закрите людиною раніше — тиша.
 * - **Куди фокус після меню** (`focusBack`): `Esc` і вибраний пункт повертають фокус не на кнопку, а
 *   туди, де живуть клавіші екрана (назва картки дошки, рядок списку); немає — на кнопку.
 * - **Кероване меню** (`Menu`): одне на таблицю, якір — кнопка рядка, який відкрили (LST-23).
 */
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { MenuItem } from './layer-types.ts';
import { useLayerModule, usePendingClose } from './Popover.tsx';

export type { MenuItem };

/** Пункти меню: масив або функція для відкритого меню; `null` — пункти ще вантажаться. */
export type MenuItems = MenuItem[] | (() => MenuItem[] | null);

/** Меню відкрилось; відхилений проміс (пункти не завантажились) закриває меню. */
export type MenuOpen = (() => void) | (() => Promise<unknown>);

/** Пункти не завантажились, а меню ще відкрите: тост власника. */
export type MenuOpenFailed = () => void;

/** Куди повернути фокус після `Esc` чи вибраного пункту; `null` — на кнопку меню. */
export type FocusBack = () => HTMLElement | null | undefined;

interface MenuButtonProps {
  label: string;
  items: MenuItems;
  className?: string;
  children: ReactNode;
  align?: 'start' | 'end';
  /** Відкрити ззовні (клавіша `S` на задачі). */
  openSignal?: number | undefined;
  /** Підказка кнопки з клавішею («Подзвонити (C)»): клавіша — тут, а не `kbd` у вузькій шапці. */
  title?: string | undefined;
  /** Клавіша кнопки для програми читання (`aria-keyshortcuts`). */
  keyshortcuts?: string | undefined;
  /** `-1` — поза порядком `Tab` (roving tabindex дошки: кнопки неактивної картки). */
  tabIndex?: number | undefined;
  /** Меню відкрилось: пункти, яким потрібні дані чи модуль, вантажать їх лише тепер («Написати ▾»). */
  onOpen?: MenuOpen | undefined;
  onOpenFailed?: MenuOpenFailed | undefined;
  focusBack?: FocusBack | undefined;
}

/**
 * Відкрите меню, лінивий модуль шарів і ліниві пункти (спільне для `MenuButton` і `Menu`). Відкрите
 * раніше за модуль чи пункти — з'явиться, щойно обоє прийдуть (фокус — на першому пункті, якщо
 * людина його нікуди не перенесла). До того `Esc` і клік поза вже закривають меню; збій — теж.
 */
function useMenuLayer(
  open: boolean,
  list: MenuItem[] | null,
  anchorRef: RefObject<HTMLButtonElement | null>,
  onClose: () => void,
  focusBack: FocusBack | undefined,
  onOpen: MenuOpen | undefined,
  onOpenFailed: MenuOpenFailed | undefined,
) {
  const layers = useLayerModule(open, onClose);
  const shown = open && layers !== null && list !== null;
  const back = useRef(focusBack);
  back.current = focusBack;
  // До модуля чи пунктів `Esc` теж повертає фокус туди, де живуть клавіші екрана (рядок списку).
  const home = useCallback(() => back.current?.(), []);
  usePendingClose(open && !shown, anchorRef, null, onClose, home);
  // Де був фокус, коли меню відкрили до приходу модуля чи пунктів (`null` — усе вже було).
  const waitedFrom = useRef<Element | null>(null);
  useLayoutEffect(() => {
    if (!open) waitedFrom.current = null;
    else if (!shown) waitedFrom.current = document.activeElement;
  }, [open, shown]);
  const opened = useRef(onOpen);
  opened.current = onOpen;
  const failed = useRef(onOpenFailed);
  failed.current = onOpenFailed;
  const shut = useRef(onClose);
  shut.current = onClose;
  useEffect(() => {
    if (!open) return;
    let live = true;
    const pending = opened.current?.();
    if (pending instanceof Promise)
      pending.catch(() => {
        if (!live) return;
        shut.current();
        failed.current?.();
      });
    return () => {
      live = false;
    };
  }, [open]);
  const close = useCallback(
    (restoreFocus: boolean) => {
      onClose();
      if (restoreFocus) (back.current?.() ?? anchorRef.current)?.focus();
    },
    [onClose, anchorRef],
  );
  const returnFocus = home;
  return { layers: shown ? layers : null, waitedFrom, close, returnFocus };
}

const itemsOf = (open: boolean, items: MenuItems) =>
  open ? (typeof items === 'function' ? items() : items) : null;

export function MenuButton({
  label,
  items,
  className,
  children,
  align = 'end',
  openSignal,
  title,
  keyshortcuts,
  tabIndex,
  onOpen,
  onOpenFailed,
  focusBack,
}: MenuButtonProps) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const shut = useCallback(() => setOpen(false), []);
  const list = itemsOf(open, items);
  const { layers, waitedFrom, close, returnFocus } = useMenuLayer(
    open,
    list,
    buttonRef,
    shut,
    focusBack,
    onOpen,
    onOpenFailed,
  );

  useEffect(() => {
    if (openSignal) setOpen(true);
  }, [openSignal]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={className}
        aria-label={label}
        title={title}
        aria-keyshortcuts={keyshortcuts}
        tabIndex={tabIndex}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open && layers ? menuId : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        {children}
      </button>
      {layers && list && (
        <layers.MenuList
          id={menuId}
          label={label}
          items={list}
          align={align}
          buttonRef={buttonRef}
          waitedFrom={waitedFrom}
          close={close}
          returnFocus={returnFocus}
        />
      )}
    </>
  );
}

export interface MenuProps {
  /** Відкрите меню: якір (`anchorRef`) уже в DOM. */
  open: boolean;
  id: string;
  label: string;
  items: MenuItems;
  align?: 'start' | 'end';
  /** Кнопка, під якою стоїть меню (кнопка «⋯» рядка). */
  anchorRef: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  focusBack?: FocusBack | undefined;
  /** Меню відкрилось: ліниві пункти вантажать свій модуль лише тепер. */
  onOpen?: MenuOpen | undefined;
  onOpenFailed?: MenuOpenFailed | undefined;
}

/**
 * Кероване меню без власної кнопки (LST-23): одне на таблицю, кнопки рядків лише кажуть, який
 * рядок відкрито (`aria-expanded`, `aria-controls` ставить власник). Поведінка — як у `MenuButton`.
 */
export function Menu({
  open,
  id,
  label,
  items,
  align = 'end',
  anchorRef,
  onClose,
  focusBack,
  onOpen,
  onOpenFailed,
}: MenuProps) {
  const list = itemsOf(open, items);
  const { layers, waitedFrom, close, returnFocus } = useMenuLayer(
    open,
    list,
    anchorRef,
    onClose,
    focusBack,
    onOpen,
    onOpenFailed,
  );
  if (!layers || !list) return null;
  return (
    <layers.MenuList
      id={id}
      label={label}
      items={list}
      align={align}
      buttonRef={anchorRef}
      waitedFrom={waitedFrom}
      close={close}
      returnFocus={returnFocus}
    />
  );
}
